import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { IAuth, ICreateUser } from '../types';

export const baseURL =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_BASE_URL_DEV
    : process.env.REACT_APP_BASE_URL;

const getToken = (): string | null => {
  try {
    const token = localStorage.getItem('token');

    return token ? JSON.parse(token).token : null;
  } catch (error) {
    return null;
  }
};

export const axiosInstance: AxiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data;
  },
  (error) => {
    if (error.response.status === 401) {
      localStorage.clear();
      window.location.href = '/login';
    }
    const errorMessage =
      error.response?.data?.message || error.message || 'An error occurred';
    return Promise.reject(new Error(errorMessage));
  },
);

export const auth = (payload: IAuth): Promise<any> =>
  axiosInstance.post('/login', payload);

export const getDevices = (params?: any): Promise<any> =>
  axiosInstance.get('/device', {
    params,
  });

export const getDeviceById = (id: string): Promise<any> =>
  axiosInstance.get(`/device/${id}`);

export const getDeviceInfo = (id: string): Promise<any> =>
  axiosInstance.get(`/device/info/${id}`);

export const saveDevice = (payload: { [key: string]: any }): Promise<any> =>
  axiosInstance.post(`/device`, payload);

export const updateDevice = (
  id: string,
  payload: { [key: string]: any },
): Promise<any> => axiosInstance.put(`/device/${id}`, payload);

export const updateDeviceProfilePicture = (
  id: string,
  file: File,
): Promise<any> => {
  const formData = new FormData();
  formData.append('picture', file);

  return axiosInstance.post(`/device/${id}/pp`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const updateAllDevices = (payload: {
  [key: string]: any;
}): Promise<any> => axiosInstance.post('/device/update-all', payload);

export const deleteDevice = (id: string): Promise<any> =>
  axiosInstance.delete(`/device/${id}`);

export const sendMessage = (id: string, payload: any): Promise<any> =>
  axiosInstance.post(`/device/sendmessage/${id}`, payload);

export const resetDevice = (id: string): Promise<any> =>
  axiosInstance.get(`/device/reset/${id}`);

export const stopDevice = (id: string): Promise<any> =>
  axiosInstance.get(`/device/stop/${id}`);

export const resetMultipleDevice = (payload: any): Promise<any> =>
  axiosInstance.post('/device/reset', payload);

export const getDeviceLogsById = (id: string): Promise<any> =>
  axiosInstance.get(`/device/${id}/logs`);

export const getDeviceMessagesById = (id: string): Promise<any> =>
  axiosInstance.get(`/device/${id}/messages`);

export const getBonusRequestsById = (id: string): Promise<any> =>
  axiosInstance.get(`/device/${id}/bonus-requests`);

export const getQuickStatistics = (): Promise<any> =>
  axiosInstance.get('/statistics/quick');

export const getStatistics = (params: { period: string }): Promise<any> =>
  axiosInstance.get('/statistics', { params });

export const getBonusRequestsStatistics = (params: {
  period: string;
}): Promise<any> => axiosInstance.get('/statistics/bonus-requests', { params });

export const getUsers = (): Promise<any> => axiosInstance.get('/user');

export const getUserById = (id: string): Promise<any> =>
  axiosInstance.get(`/user/${id}`);

export const saveUser = (payload: ICreateUser): Promise<any> =>
  axiosInstance.post('/user', payload);

export const updateUser = (id: string, payload: any): Promise<any> =>
  axiosInstance.put(`/user/${id}`, payload);

export const deleteUser = (id: string): Promise<any> =>
  axiosInstance.delete(`/user/${id}`);

export const getMessageTemplates = (params?: {
  siteIds?: number[];
}): Promise<any> => axiosInstance.get('/message-template', { params });

export const addMessageTemplate = (payload: {
  [key: string]: any;
}): Promise<any> => axiosInstance.post('/message-template', payload);

export const getMessageTemplateById = (id: string): Promise<any> =>
  axiosInstance.get(`/message-template/${id}`);

export const deleteMessageTemplate = (id: string): Promise<any> =>
  axiosInstance.delete(`/message-template/${id}`);

export const updateMessageTemplate = (
  id: string,
  payload: { [key: string]: any },
): Promise<any> => axiosInstance.put(`/message-template/${id}`, payload);

export const getSites = (): Promise<any> => axiosInstance.get('/site');

export const addSite = (payload: { [key: string]: any }): Promise<any> =>
  axiosInstance.post('/site', payload);

export const getSiteById = (id: string): Promise<any> =>
  axiosInstance.get(`/site/${id}`);

export const deleteSite = (id: string): Promise<any> =>
  axiosInstance.delete(`/site/${id}`);

export const updateSite = (
  id: string,
  payload: { [key: string]: any },
): Promise<any> => axiosInstance.put(`/site/${id}`, payload);

export const getFilterProxyList = (): Promise<any> =>
  axiosInstance.get('/filter/proxy-list');

export const getNumbers = (params: {
  period?: string;
  sortBy?: string;
  number?: string;
  numberType?: string;
  sortOrder: 'ASC' | 'DESC';
}): Promise<any> => axiosInstance.get('/numbers', { params });

export const getNumberStatistics = (params: {
  period?: string;
}): Promise<any> => axiosInstance.get('/numbers/statistics', { params });

export const getVirtualNumberCountries = (
  service: number,
  params: {
    verification?: boolean;
  },
): Promise<any> =>
  axiosInstance.get(`/virtual-number/countries/${service}`, { params });

export const getVirtualNumber = (payload: any, service: number): Promise<any> =>
  axiosInstance.post(`/virtual-number/numbers/${service}`, payload);

export const getVirtualNumberActivations = (): Promise<any> => {
  return axiosInstance.get('/virtual-number/activations');
};

export const selectFavoriteCountry = (
  countryId: any,
  service: number,
): Promise<any> => {
  return axiosInstance.post(`/virtual-number/favorites/${service}`, {
    countryId,
  });
};

export const removeFavoriteCountry = (
  countryId: any,
  service: number,
): Promise<any> => {
  return axiosInstance.delete(`/virtual-number/favorites/${service}`, {
    data: {
      countryId,
    },
  });
};

export const updateStatusVirtualNumber = (
  id: string,
  payload: any,
): Promise<any> => axiosInstance.put(`/virtual-number/numbers/${id}`, payload);

export const virtualNumberBalance = (): Promise<any> =>
  axiosInstance.get(`/virtual-number/balance`);

export const getQueueSchedule = (): Promise<any> =>
  axiosInstance.get('/device/queue-schedule');

export const updateQueueSchedule = (payload: {
  enabled: boolean;
}): Promise<any> => axiosInstance.put('/device/queue-schedule', payload);

export const getQueueDistributions = (): Promise<any> =>
  axiosInstance.get('/queue-distribution');

export const applyQueueDistribution = (payload: any): Promise<any> =>
  axiosInstance.post(`/queue-distribution`, payload);

export const getDeviceRedirectGroups = (): Promise<any> =>
  axiosInstance.get('/device-redirect-group');

export const addDeviceRedirectGroup = (payload: any): Promise<any> =>
  axiosInstance.post('/device-redirect-group', payload);

export const automaticDistribution = (): Promise<any> =>
  axiosInstance.get('/device-redirect-group/auto-distribute-bots');

export const updateDeviceRedirectGroup = (
  id: string,
  payload: any,
): Promise<any> => axiosInstance.put(`/device-redirect-group/${id}`, payload);

export const deleteDeviceRedirectGroup = (id: string): Promise<any> =>
  axiosInstance.delete(`/device-redirect-group/${id}`);

export const telegramSendCode = (id: string, payload: any): Promise<any> =>
  axiosInstance.post(`/device/telegram/send-code/${id}`, payload);

export const telegramSignIn = (id: string, payload: any): Promise<any> =>
  axiosInstance.post(`/device/telegram/sign-in/${id}`, payload);

// TODO: Add other telegram methods
